import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, AlertTriangle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * UK FSMA financial-promotion style risk banner.
 * Persistently visible on every public page until dismissed for the session.
 * Also links out to the full /risk-warning page for statutory-length wording.
 */
export const RiskBanner = () => {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(sessionStorage.getItem("titan_risk_dismissed") !== "1");
  }, []);

  const dismiss = () => {
    sessionStorage.setItem("titan_risk_dismissed", "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <aside
      data-testid="risk-banner"
      role="region"
      aria-label="Risk warning"
      className="fixed bottom-0 inset-x-0 z-40 bg-zinc-900 text-zinc-100 border-t border-white/5"
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-2.5 md:py-3 flex items-start md:items-center gap-3 md:gap-4">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 md:mt-0 text-amber-400" />
        <p className="text-[11px] md:text-[12px] leading-relaxed flex-1 min-w-0">
          <span className="font-medium">{t("risk.headline")}</span>{" "}
          <span className="text-zinc-400 hidden sm:inline">{t("risk.body")}</span>{" "}
          <Link to="/risk-warning" data-testid="risk-banner-link" className="underline underline-offset-2 hover:text-white text-zinc-300">
            {t("risk.readMore")}
          </Link>
        </p>
        <button
          data-testid="risk-banner-dismiss"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </aside>
  );
};
